import { OsCommonContext, OsContextApi } from '@wppopen/core';
import { connectToParent, AsyncMethodReturns } from 'penpal';
import { environment } from '../../../environments/environment';
import { Inject, Injectable, InjectionToken } from '@angular/core';

export interface WppOpenBrowserWindow {
	readonly self: unknown;
	readonly top: unknown;
	readonly parent: { readonly location: Pick<Location, 'origin'> };
	readonly location: Pick<Location, 'origin' | 'pathname'>;
}

type WppOpenParentApi = Pick<AsyncMethodReturns<OsContextApi>, 'getAccessToken'>;

interface WppOpenConnection {
	readonly promise: Promise<{ osApi?: WppOpenParentApi }>;
}

interface WppOpenConnectionOptions {
	readonly parentOrigin: string;
	readonly methods: { receiveOsContext: (context: OsCommonContext) => void };
	readonly debug: boolean;
}

export type WppOpenParentConnector = (options: WppOpenConnectionOptions) => WppOpenConnection;

export const WPP_OPEN_WINDOW = new InjectionToken<WppOpenBrowserWindow>('WPP Open browser window', {
	providedIn: 'root',
	factory: () => window
});

export const WPP_OPEN_PARENT_CONNECTOR = new InjectionToken<WppOpenParentConnector>('WPP Open parent connector', {
	providedIn: 'root',
	factory: () => options => connectToParent<{ osApi: OsContextApi }>(options)
});

@Injectable({
	providedIn: 'root'
})
export class WppOpenService {
	private _connection: WppOpenConnection | undefined;
	private _contextPromise: Promise<OsCommonContext> | undefined = undefined;

	private readonly config = {
		parentOrigin: environment?.wppOpenParentOrigin?.length ? environment.wppOpenParentOrigin : '*',
		debug: !!environment?.wppOpenDebug || false
	};

	constructor(
		@Inject(WPP_OPEN_WINDOW) private readonly browserWindow: WppOpenBrowserWindow,
		@Inject(WPP_OPEN_PARENT_CONNECTOR) private readonly connectToParent: WppOpenParentConnector
	) {}

	public async initialize(): Promise<void> {
		if (!this.isWppOpenEmbeddedFrame() || this._connection?.promise) {
			return;
		}

		console.log('Connecting to iframe parent...');
		this._contextPromise = new Promise(async (resolve, reject) => {
			this._connection = this.connectToParent({
				parentOrigin: this.config.parentOrigin,
				methods: {
					receiveOsContext: (context: OsCommonContext) => {
						console.error('RECEIVED CONTEXT VVVVV');
						console.log(context);
						resolve(context);
					}
				},
				debug: this.config.debug
			});
			this._connection.promise.then(connection => {
				const osApiAvailable = !!connection.osApi;
				console.log(
					osApiAvailable
						? 'Connection to iframe established - OS API available'
						: 'Connection to iframe established - OS API not available'
				);
			});
		});
	}

	private async getOsApi(): Promise<WppOpenParentApi | null> {
		if (!this._connection?.promise) {
			return null;
		}
		const connection = await this._connection.promise;
		if (!connection.osApi) {
			return null;
		}
		return connection.osApi;
	}

	public async wppOpenApiAvailable(): Promise<boolean> {
		const osApi = await this.getOsApi();
		return !!osApi;
	}

	public async getAccessToken(): Promise<string> {
		const osApi = await this.getOsApi();
		if (!osApi) {
			throw new Error('WPP Open API not available.');
		}

		const accessToken = await osApi.getAccessToken();
		if (!accessToken) {
			throw new Error('Failed to get access token.');
		}

		return accessToken;
	}

	public async getOsContext(): Promise<OsCommonContext | null> {
		const osApi = await this.getOsApi();
		if (!osApi || !this._contextPromise) {
			return null;
		}
		return this._contextPromise;
	}

	public async getWorkspaceScope() {
		const context = await this.getOsContext();
		if (!context) {
			throw new Error('WPP Open context not available.');
		}

		const hierarchy = context.hierarchy;
		const tenantId = context.tenant?.id;
		const tenantAzId = context.tenant?.azId;

		// Check if tenant-level assignment (hierarchy matches tenant)
		if (hierarchy?.azId === tenantAzId) {
			console.log('Tenant-level assignment detected, tenantId:', tenantId);
			return {
				workspaceId: undefined,
				scopeId: undefined,
				tenantId
			};
		}

		const workspaceId = hierarchy?.azId;

		const scopeId = Object.values(hierarchy?.mapping ?? {}).find(v => !v.parentAzId)?.azId;

		return {
			workspaceId,
			scopeId,
			tenantId
		};
	}

	private isWppOpenEmbeddedFrame(): boolean {
		try {
			return (
				this.browserWindow.self !== this.browserWindow.top &&
				!this.browserWindow.location.pathname.startsWith('/widget/') &&
				!this.isParentSameOrigin()
			);
		} catch (error) {
			return true;
		}
	}

	/**
	 * True when the immediate parent frame is another instance of this app (same origin),
	 * e.g. the editor's package-preview iframe at /editor/preview/:id.
	 *
	 * Connecting penpal to a same-origin parent is never valid: that parent is not the
	 * WPP Open shell, and the extra penpal call receiver registered by this service also
	 * answers the parent's penpal calls that are meant for the page-level connection
	 * (e.g. requestPreviewForExport), rejecting them with
	 * "TypeError: Cannot read properties of undefined (reading 'apply')" (penpal v6 does
	 * not multiplex connections between the same window pair).
	 *
	 * The real WPP Open shell is cross-origin, so accessing parent.location throws and
	 * this returns false, keeping the shell connection behavior unchanged.
	 */
	private isParentSameOrigin(): boolean {
		try {
			return (
				this.browserWindow.parent !== this.browserWindow.self &&
				this.browserWindow.parent.location.origin === this.browserWindow.location.origin
			);
		} catch (error) {
			return false;
		}
	}
}
